import { getUnixTime } from "date-fns/fp";
// dinero
import {
  convert,
  Currency,
  dinero,
  Dinero,
  normalizeScale,
  toSnapshot,
} from "dinero.js";
import { findClosest } from "find-closest";
import maxBy from "lodash/fp/maxBy";
import values from "lodash/fp/values";
import { createCachedSelector } from "re-reselect";
import { createSelector } from "reselect";
// import { call, select } from 'redux-saga/effects';
import { select } from "starfx";

import { schema } from "@app/statefx/schema";
import { IExchangeRate } from "@app/types/insider/currency";
import { CHF, EUR, GBP, MDL, RON, USD } from "@dinero.js/currencies";

import { TCurrency } from "../../types";
import { AppState } from "../xstore";

import type { TCurrencyRecordSet } from "@app/types/insider/currency";
export const dineros = {
  EUR: EUR,
  RON: RON,
  USD: USD,
  GBP: GBP,
  CHF: CHF,
  MDL: MDL,
};
const zero = dinero({ amount: 0, currency: RON, scale: 4 });

/* -------------------------------------------------------------------------- */
/*                                THE CONVERTER                               */
/* -------------------------------------------------------------------------- */

export function convnertDinero(
  dineroObject: Dinero<number>,
  newCurrency: Currency<number>,
  rates: Record<TCurrency, { amount: number; scale: number }>
) {
  const [source] = normalizeScale([dineroObject, zero]);
  const { currency: fromCurrency } = toSnapshot(source);
  if (fromCurrency.code === newCurrency.code) return dineroObject;
  const fromCCyCode = fromCurrency.code as TCurrency;
  const toCCyCode = newCurrency.code as TCurrency;
  const parity = Math.floor(
    rates[fromCCyCode].amount * (1 / rates[toCCyCode].amount) * 10000
  );
  const newDinero = convert(dineroObject, newCurrency, {
    [toCCyCode]: { amount: parity, scale: 4 },
  });
  return newDinero;
}
export function* convertAmmountToDinero(
  amountRaw: number,
  fromCurrency: TCurrency,
  toCurrency: TCurrency,
  date: string
) {
  const rates_ = yield* select((s) =>
    currParitiesOnClosestDateOf_select(s, { date: date || null })
  ) as any;
  const rates = rates_ as Record<TCurrency, { amount: number; scale: number }>;
  const fromDineroCurrency = dineros[fromCurrency] as any;
  const toDineroCurrency = dineros[toCurrency];

  const newDinero = convnertDinero(
    dinero({
      amount: amountRaw * 10000,
      currency: fromDineroCurrency,
      scale: 4,
    }),
    toDineroCurrency,
    rates_
  );
  return newDinero;
}

export function convertAmmountToDineroFn(
  amountRaw: number,
  fromCurrency: TCurrency,
  toCurrency: TCurrency,
  rates:
    | Record<TCurrency, { amount: number; scale: number }>
    | TCurrencyRecordSet
) {
  if (!fromCurrency || !toCurrency || !rates) {
    console.error("convertAmmountToDineroFn: rates not found");
    return dinero({
      amount: 0,
      currency: dineros?.[toCurrency || ("RON" as TCurrency)],
      scale: 4,
    });
  }
  try {
    const newDinero = convnertDinero(
      dinero({
        amount: Math.floor((amountRaw || 0) * 10000),
        currency: dineros[fromCurrency],
        scale: 4,
      }),
      dineros[toCurrency],
      rates
    );
    return newDinero;
  } catch (error) {
    console.log("error", error);
    console.error(
      "convertAmmountToDineroFn",
      amountRaw,
      amountRaw * 10000,
      fromCurrency,
      toCurrency,
      rates
    );
    return dinero({ amount: 0, currency: dineros[toCurrency], scale: 4 });
  }
}

//  Selectors //
type ExDinero = Record<TCurrency, { amount: number; scale: number }>;
type ExDineroRecords = Record<string, ExDinero>;
//   const rateSelectors = repo.getSelectors((s: RootState) => s[REPO_CURRENCY]);

export const selectRates = createSelector(
  schema.currencyRates.selectTable,
  (s) => s
);
export const selectedIdxRates = createSelector(
  schema.currencyRates.selectTableAsList,
  (s) => {
    const data = s.reduce((acc: ExDineroRecords, data: IExchangeRate) => {
      acc[data.XRDATESTRING] = {
        RON: { amount: Math.floor(Number(data?.RON || 1) * 10000), scale: 4 },
        EUR: { amount: Math.floor(Number(data?.EUR || 0) * 10000), scale: 4 },
        GBP: { amount: Math.floor(Number(data?.GBP || 0) * 10000), scale: 4 },
        USD: { amount: Math.floor(Number(data?.USD || 0) * 10000), scale: 4 },
        CHF: { amount: Math.floor(Number(data?.CHF || 0) * 10000), scale: 4 },
        MDL: { amount: Math.floor(Number(data?.MDL || 1) * 10000), scale: 4 },
      };
      return acc;
    }, {});
    return data;
  }
);

export const selectRatesToday = createSelector(selectedIdxRates, (s) => {
  return s[Object.keys(s).pop() || ""] || {};
});

function createParameterSelector(selector: any) {
  return (_: AppState, params: any) => selector(params);
}
const getDate = createParameterSelector(
  (params: { date: string }) => params?.date || null
);
const getFromCurrency = createParameterSelector(
  (params: { fromCurrencyString: TCurrency | null }) =>
    params.fromCurrencyString || "EUR"
);
const geToCurrency = createParameterSelector(
  (params: { toCurrencyString: TCurrency | null }) =>
    params.toCurrencyString || "EUR"
);
const getAmount = createParameterSelector(
  (params: { amountRaw: number }) => params.amountRaw || 0
);

export const selectClosestExchangeRate = createSelector(
  selectedIdxRates,
  getDate,
  (rates, targetDate) => {
    let closestDate = "";
    let minDiff = Infinity;

    Object.keys(rates).forEach((dateString) => {
      const date = new Date(dateString);
      const diff = Math.abs(new Date(targetDate).getTime() - date.getTime());

      if (diff < minDiff) {
        minDiff = diff;
        closestDate = dateString;
      }
    });

    return { ...(rates[closestDate] || {}), XRDATESTRING: closestDate };
  }
);
/* -------------------------------------------------------------------------- */
/*                                THE CONVERTER                               */
/* -------------------------------------------------------------------------- */

//   function convnertDinero(dineroObject, newCurrency, rates) {
// 	const [source] = normalizeScale([dineroObject, zero]);
// 	const { currency: fromCurrency } = toSnapshot(source);
// 	if (fromCurrency.code === newCurrency.code) return dineroObject;
// 	const parity = Math.floor(
// 	  rates[fromCurrency.code].amount *
// 		(1 / rates[newCurrency.code].amount) *
// 		10000,
// 	);
// 	const newDinero = convert(dineroObject, newCurrency, {
// 	  [newCurrency.code]: { amount: parity, scale: 4 },
// 	});
// 	return newDinero;
//   }
/* -------------------------------------------------------------------------- */
/*                                     ***                                    */
/* -------------------------------------------------------------------------- */

export const ivCalRate_mostRecentRate_select = createSelector(
  schema.currencyRates.selectTable,
  (rates) => {
    const mxr = maxBy(
      (x: IExchangeRate) => x.XRDATEUNIX,
      Object.values(rates) as IExchangeRate[]
    );
    return mxr;
  }
);

export const ivCalRate_ratesOfDate_select = createCachedSelector(
  getDate,
  schema.currencyRates.selectTable,
  (dt, rates) => {
    // has wait for !!  do not return null unless you want to hang forever //
    const dtUx = getUnixTime(new Date(dt));
    const closestRate = findClosest(
      values(rates),
      dtUx,
      ({ XRDATEUNIX }) => +XRDATEUNIX
    );
    return closestRate;
  }
)((_, p) => JSON.stringify(new Date(p.date).toLocaleDateString() || null)); // ATENTION: this is a KEY so do not use time expression ///

export const currExRatesClosestToDateOf_select = createCachedSelector(
  [getDate, ivCalRate_ratesOfDate_select, ivCalRate_mostRecentRate_select],
  (date, closest, mxr) => {
    if (!date) return mxr;
    return closest;
  }
)(
  (_, p) =>
    `currExRatesClosestToDateOf_select_${JSON.stringify(
      new Date(p.date).toLocaleDateString() || null
    )}`
); // ATENTION: this is a KEY so do not use time expression ///

export const currParitiesOnClosestDateOf_select = createCachedSelector(
  [getDate, currExRatesClosestToDateOf_select],
  (date, curs) => {
    const tmp = "0.0000";
    return {
      RON: {
        amount: Math.floor(((curs?.RON || 1) as number) * 10000),
        scale: 4,
      }, // important
      EUR: {
        amount: Math.floor(((curs?.EUR || tmp) as unknown as number) * 10000),
        scale: 4,
      },
      GBP: {
        amount: Math.floor(((curs?.GBP || tmp) as unknown as number) * 10000),
        scale: 4,
      },
      USD: {
        amount: Math.floor(((curs?.USD || tmp) as unknown as number) * 10000),
        scale: 4,
      },
      CHF: {
        amount: Math.floor(((curs?.CHF || tmp) as unknown as number) * 10000),
        scale: 4,
      },
    };
  }
)(
  (_, p) =>
    `CPOCDOS::${JSON.stringify(new Date(p.date).toLocaleDateString() || null)}`
);

export const currencyHasBeenLoaded = createSelector(
  schema.currencyRates.selectTable,
  (rates) => rates && Object.keys(rates).length > 0
);

/* -------------------------------------------------------------------------- */
/*                                    extra                                   */
/* -------------------------------------------------------------------------- */

// DO NOT USE UNLESS BADLY NEEDED. will memoize every little operation...
// USE convertAmmountToDinero from currency_saga.ts
export const convertAmmountByCurrencyByDateToDinero_select =
  createCachedSelector(
    [
      getFromCurrency,
      geToCurrency,
      getAmount,
      getDate,
      currExRatesClosestToDateOf_select,
    ],
    (
      fromCurrency: TCurrency,
      toCurrency: TCurrency,
      amountRaw: number,
      date: string,
      curs: IExchangeRate
    ) => {
      const tmp = "0.0000";
      const rates = {
        RON: {
          amount: Math.floor(((curs?.RON || 1) as number) * 10000),
          scale: 4,
        }, // important
        EUR: {
          amount: Math.floor(((curs?.EUR || tmp) as unknown as number) * 10000),
          scale: 4,
        },
        GBP: {
          amount: Math.floor(((curs?.GBP || tmp) as unknown as number) * 10000),
          scale: 4,
        },
        USD: {
          amount: Math.floor(((curs?.USD || tmp) as unknown as number) * 10000),
          scale: 4,
        },
        CHF: {
          amount: Math.floor(((curs?.CHF || tmp) as unknown as number) * 10000),
          scale: 4,
        },
      };
      //
      const newDinero = convnertDinero(
        dinero({
          amount: amountRaw * 10000,
          currency: dineros[fromCurrency],
          scale: 4,
        }),
        dineros[toCurrency],
        rates as any
      );
      return newDinero;
    }
  )((_, p) =>
    [
      "C2CDS",
      p.fromCurrency?.code || p.fromCurrency,
      p.toCurrency?.code || p.toCurrency,
      p.amountRaw,
      p.date || new Date().toLocaleDateString(),
    ].join("_")
  );

// todo convert dinreo to dinero //

export const getEmitentAbreviation = createParameterSelector(
  (params: any): string | null => params?.ABR || null
);

export const selectAccountsByEmitent = createSelector(
  schema.issuerBankAccounts.selectTableAsList,
  schema.invoiceIssuer.selectTableAsList,
  getEmitentAbreviation,
  (accounts, issuers, abrv) => {
    const issuer = issuers.find((x) => x.ABR === abrv);
    if (!issuer) return [];
    return accounts.filter((x) => x.EID === issuer.E_ID);
  }
);
